/**
 * Appレイアウトコンポーネント
 * Sidebar + ContentArea の全体レイアウトを担当
 */

import { useCallback } from 'react';
import { Box } from '@chakra-ui/react';
import { AgeGroup, SessionInfo, SessionInitStatus } from '../../../types';
import { Sidebar } from '@/components/layouts/Sidebar';
import { ContentArea } from './ContentArea';
import { SessionStatusGate } from '@/components/error/SessionStatusGate';
import { HeaderActions } from '@/components/layouts/HeaderActions';
import { UsageGuideDialog } from '@/components/dialog/UsageGuideDialog';
import type { UsageGuideState } from '../../../hooks/useUsageGuide';

interface AppLayoutProps {
  // 状態
  sidebarOpen: boolean;
  sessionId: string | null;
  ageGroup: AgeGroup | null;
  initStatus: SessionInitStatus;
  chatHistory: SessionInfo[];

  // ハンドラー
  handleToggleSidebar: () => void;
  handleCloseSidebar: () => void;
  loadChatHistory: () => Promise<void>;
  handleNewChat: () => Promise<void>;
  handleSelectChat: (sessionId: string) => void;
  handleDeleteSession: (sessionId: string) => Promise<void>;

  // 使い方モーダル
  usageGuide: UsageGuideState;
}

export const AppLayout = ({
  sidebarOpen,
  sessionId,
  ageGroup,
  initStatus,
  chatHistory,
  handleToggleSidebar,
  handleCloseSidebar,
  loadChatHistory,
  handleNewChat,
  handleSelectChat,
  handleDeleteSession,
  usageGuide,
}: AppLayoutProps) => {
  const handleNewChatAndClose = useCallback(async () => {
    await handleNewChat();
    handleCloseSidebar();
  }, [handleNewChat, handleCloseSidebar]);

  const handleSelectChatAndClose = useCallback(
    (selectedSessionId: string) => {
      handleSelectChat(selectedSessionId);
      handleCloseSidebar();
    },
    [handleSelectChat, handleCloseSidebar]
  );

  if (initStatus !== SessionInitStatus.READY) {
    return (
      <Box h="100vh" bg="whiteAlpha.500">
        <SessionStatusGate initStatus={initStatus}>{null}</SessionStatusGate>
      </Box>
    );
  }

  return (
    <>
      <HeaderActions onOpenUsageGuide={usageGuide.openGuide} />
      <UsageGuideDialog
        isOpen={usageGuide.isOpen}
        isRequired={usageGuide.isRequired}
        onClose={usageGuide.closeGuide}
        onAcknowledge={usageGuide.acknowledge}
      />
      <Box display="flex" h="100vh" overflow="hidden" bg="gradientSecondary">
        <Sidebar
          isOpen={sidebarOpen}
          onToggle={handleToggleSidebar}
          onNewChat={handleNewChatAndClose}
          ageGroup={ageGroup!}
          chatHistory={chatHistory}
          currentSessionId={sessionId!}
          onSelectChat={handleSelectChatAndClose}
          onDeleteSession={handleDeleteSession}
        />
        <ContentArea
          sessionId={sessionId!}
          ageGroup={ageGroup!}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={handleToggleSidebar}
          onMessageSent={loadChatHistory}
        />
      </Box>
    </>
  );
};
